import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Target } from 'lucide-react';

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
}

interface DynamicBeltDisplayProps {
  currentBelt?: BeltLevel | null;
  nextBelt?: BeltLevel | null;
}

const DynamicBeltDisplay: React.FC<DynamicBeltDisplayProps> = ({
  currentBelt,
  nextBelt
}) => {
  useUiLanguage();
  const getBeltColorClass = (color: string, beltName: string = '') => {
    // If color is already a Tailwind class (from database), use it directly
    if (color && (color.startsWith('bg-') || color.includes('text-'))) {
      return color;
    }

    // Fallback: extract color from belt name for proper martial arts colors
    const nameStr = beltName?.toLowerCase() || '';
    const colorStr = color?.toLowerCase() || '';

    // Enhanced color matching for martial arts belts
    if (nameStr.includes('white')) return 'bg-white text-gray-900 border-2 border-gray-400 font-semibold';
    if (nameStr.includes('orange')) return 'bg-orange-600 text-white font-semibold';
    if (nameStr.includes('blue')) return 'bg-blue-600 text-white font-semibold';
    if (nameStr.includes('yellow')) return 'bg-yellow-500 text-gray-900 font-semibold';
    if (nameStr.includes('green')) return 'bg-green-600 text-white font-semibold';
    if (nameStr.includes('purple')) return 'bg-purple-600 text-white font-semibold';
    if (nameStr.includes('brown')) return 'bg-amber-800 text-white font-semibold';
    if (nameStr.includes('red')) return 'bg-red-600 text-white font-semibold';
    if (nameStr.includes('black')) return 'bg-gray-900 text-white font-semibold';

    // Fallback based on color field
    if (colorStr.includes('white')) return 'bg-white text-gray-900 border-2 border-gray-400 font-semibold';
    if (colorStr.includes('orange')) return 'bg-orange-600 text-white font-semibold';
    if (colorStr.includes('blue')) return 'bg-blue-600 text-white font-semibold';
    if (colorStr.includes('yellow')) return 'bg-yellow-500 text-gray-900 font-semibold';
    if (colorStr.includes('green')) return 'bg-green-600 text-white font-semibold';
    if (colorStr.includes('purple')) return 'bg-purple-600 text-white font-semibold';
    if (colorStr.includes('brown') || colorStr.includes('amber')) return 'bg-amber-800 text-white font-semibold';
    if (colorStr.includes('red')) return 'bg-red-600 text-white font-semibold';
    if (colorStr.includes('black')) return 'bg-gray-900 text-white font-semibold';

    return 'bg-gray-600 text-white font-semibold';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Current Belt */}
      <Card className="bg-gradient-to-r from-red-50 to-red-100 border-red-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5 text-red-700" />
            {uiText("Your Current Level")}</CardTitle>
        </CardHeader>
        <CardContent>
          {currentBelt ? (
            <div className="space-y-2">
              <Badge className={`${getBeltColorClass(currentBelt.color, currentBelt.name)} text-sm font-medium px-3 py-1`}>
                {currentBelt.name}
              </Badge>
              <CardDescription className="text-red-700 font-medium">
                {uiText("Achieved Belt Level")}</CardDescription>
            </div>
          ) : (
            <div className="space-y-2">
              <Badge variant="outline" className="text-sm font-medium px-3 py-1">
                {uiText("No Belt Assigned")}</Badge>
              <CardDescription className="text-red-700">
                {uiText("Contact your instructor to get your belt level assigned")}</CardDescription>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Next Goal */}
      <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-orange-700" />
            {uiText("Next Goal")}</CardTitle>
        </CardHeader>
        <CardContent>
          {nextBelt ? (
            <div className="space-y-2">
              <Badge className={`${getBeltColorClass(nextBelt.color, nextBelt.name)} text-sm font-medium px-3 py-1`}>
                {nextBelt.name}
              </Badge>
              <CardDescription className="text-orange-700 font-medium">
                {uiText("Work towards this level")}</CardDescription>
            </div>
          ) : (
            <div className="space-y-2">
              <Badge variant="outline" className="text-sm font-medium px-3 py-1">
                {currentBelt ? uiText("Highest Level Achieved") : uiText("First Belt Goal")}
              </Badge>
              <CardDescription className="text-orange-700">
                {currentBelt
                  ? uiText("You've reached the highest level! Continue training for advanced techniques")
                  : uiText("Start your journey with your first belt level")
                }
              </CardDescription>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DynamicBeltDisplay;
